import Industrial from '@assets/icons/markets/agri_industrial.svg?react';
import EnergyStorage from '@assets/icons/markets/energy_storage_systems.svg?react';
import LeisureMarine from '@assets/icons/markets/leisure_marine.svg?react';
import ProfessionalMarine from '@assets/icons/markets/marine.svg?react';
import Mobility from '@assets/icons/markets/mobility.svg?react';
import OffgridBackup from '@assets/icons/markets/offgrid_backup.svg?react';
import ProfessionalVehicles from '@assets/icons/markets/professional_vehicles.svg?react';
import RecreationalVehicles from '@assets/icons/markets/recreational_vehicles.svg?react';
import Telecom from '@assets/icons/markets/telecom.svg?react';
import { Filter, Option, SelectOption, TableHeader } from '@common/types/common';
import { IUser } from '@common/types/user';
import { Flag } from '@components/display/images';
import {
    BEAttendee,
    BlacklistTableHeader,
    EventLevelBadgeRequirement,
    EventLocation,
    EventPrinter,
    EventTypes,
    IAttendee,
    IEventCreation,
    IEventUser,
    ISubscriberActivity,
    LogicalOperator,
    ManagementAnyEvent,
    Markets,
    MarketSlugs,
    PresentStatus,
    RegistrationApplicationStatus,
    RegistrationEventListItem,
    RegistrationLabel,
    RegistrationStatus,
    WaitingListEntry,
} from '@event/types';
import { ChipProps, ListItemText } from '@mui/material';
import { capitalizeFirstLetter } from '@utils/common';
import pluralize from 'pluralize';
import { JSX } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';

const timezoneOffsetRegex = /([+-]\d{2}:\d{2}|Z)$/;

const getEventTypeShortLabel = (eventType?: EventTypes): JSX.Element => {
    switch (eventType) {
        case EventTypes.PUBLIC_ONSITE:
        case EventTypes.PRIVATE_ONSITE:
            return (
                <FormattedMessage
                    id="events.types.onsite_short"
                    defaultMessage="Onsite Event"
                    description="Short label for any onsite event (public or private)"
                />
            );
        case EventTypes.PUBLIC_VIRTUAL:
        case EventTypes.PRIVATE_VIRTUAL:
            return (
                <FormattedMessage
                    id="events.types.virtual_short"
                    defaultMessage="Virtual Event"
                    description="Short label for any virtual event (public or private)"
                />
            );
        default:
            return <></>;
    }
};

const getEventTypeTitle = (intl: IntlShape, eventType?: EventTypes) => {
    switch (eventType) {
        case EventTypes.PUBLIC_ONSITE:
            return intl.formatMessage({
                id: 'events.types.labels.public_onsite',
                defaultMessage: 'Public Onsite Event',
                description: 'Label for public onsite events',
            });

        case EventTypes.PRIVATE_ONSITE:
            return intl.formatMessage({
                id: 'events.types.labels.private_onsite',
                defaultMessage: 'Private Onsite Event',
                description: 'Label for private onsite events',
            });
        case EventTypes.PUBLIC_VIRTUAL:
            return intl.formatMessage({
                id: 'events.types.labels.public_virtual',
                defaultMessage: 'Public Virtual Event',
                description: 'Label for public virtual events',
            });
        case EventTypes.PRIVATE_VIRTUAL:
            return intl.formatMessage({
                id: 'events.types.labels.private_virtual',
                defaultMessage: 'Private Virtual Event',
                description: 'Label for private virtual events',
            });
        default:
            return '';
    }
};

const getUntranslatedEventTypeTitle = (eventType?: EventTypes) => {
    switch (eventType) {
        case EventTypes.PUBLIC_ONSITE:
            return 'Public Onsite Event';
        case EventTypes.PRIVATE_ONSITE:
            return 'Private Onsite Event';
        case EventTypes.PUBLIC_VIRTUAL:
            return 'Public Virtual Event';
        case EventTypes.PRIVATE_VIRTUAL:
            return 'Private Virtual Event';
        default:
            return '';
    }
};

const getEventTypeName = (eventType?: EventTypes): JSX.Element => {
    switch (eventType) {
        case EventTypes.PUBLIC_ONSITE:
            return (
                <FormattedMessage
                    id="events.types.titles.public_onsite"
                    defaultMessage="Public - Onsite Event"
                    description="Label for public onsite events"
                />
            );
        case EventTypes.PRIVATE_ONSITE:
            return (
                <FormattedMessage
                    id="events.types.titles.private_onsite"
                    defaultMessage="Private - Onsite Event"
                    description="Title for private onsite events"
                />
            );
        case EventTypes.PUBLIC_VIRTUAL:
            return (
                <FormattedMessage
                    id="events.types.titles.public_virtual"
                    defaultMessage="Public - Virtual Event"
                    description="Title for public virtual events"
                />
            );
        case EventTypes.PRIVATE_VIRTUAL:
            return (
                <FormattedMessage
                    id="events.types.titles.private_virtual"
                    defaultMessage="Private - Virtual Event"
                    description="Title for private virtual events"
                />
            );
        default:
            return <></>;
    }
};

const getLanguageSpokenLabel = (languageCode: string, intl: IntlShape): string => {
    // Map language codes to their translated names with explicit string IDs
    switch (languageCode) {
        case 'en':
            return intl.formatMessage({
                id: 'languages.en',
                defaultMessage: 'English',
                description: 'Language name for English',
            });
        case 'es':
            return intl.formatMessage({
                id: 'languages.es',
                defaultMessage: 'Spanish',
                description: 'Language name for Spanish',
            });
        case 'fr':
            return intl.formatMessage({
                id: 'languages.fr',
                defaultMessage: 'French',
                description: 'Language name for French',
            });
        case 'de':
            return intl.formatMessage({
                id: 'languages.de',
                defaultMessage: 'German',
                description: 'Language name for German',
            });
        case 'it':
            return intl.formatMessage({
                id: 'languages.it',
                defaultMessage: 'Italian',
                description: 'Language name for Italian',
            });
        case 'nl':
            return intl.formatMessage({
                id: 'languages.nl',
                defaultMessage: 'Dutch',
                description: 'Language name for Dutch',
            });
        case 'ru':
            return intl.formatMessage({
                id: 'languages.ru',
                defaultMessage: 'Russian',
                description: 'Language name for Russian',
            });
        default:
            return languageCode.toUpperCase();
    }
};

const getMarketLabel = (market: Markets, intl: IntlShape): string => {
    switch (market) {
        case Markets.PROFESSIONAL_VEHICLES:
            return intl.formatMessage({
                id: 'markets.professional_vehicles',
                defaultMessage: 'Professional Vehicles',
                description: 'Market label for professional vehicles industry',
            });
        case Markets.RECREATIONAL_VEHICLES:
            return intl.formatMessage({
                id: 'markets.recreational_vehicles',
                defaultMessage: 'Recreational Vehicles',
                description: 'Market label for recreational vehicles industry',
            });
        case Markets.PROFESSIONAL_MARINE:
            return intl.formatMessage({
                id: 'markets.professional_marine',
                defaultMessage: 'Professional Marine',
                description: 'Market label for professional marine industry',
            });
        case Markets.LEISURE_MARINE:
            return intl.formatMessage({
                id: 'markets.leisure_marine',
                defaultMessage: 'Leisure Marine',
                description: 'Market label for leisure marine industry',
            });
        case Markets.OFFGRID_BACKUP:
            return intl.formatMessage({
                id: 'markets.offgrid_backup',
                defaultMessage: 'Off-grid and Backup',
                description: 'Market label for off-grid and backup power industry',
            });
        case Markets.ENERGY_STORAGE:
            return intl.formatMessage({
                id: 'markets.energy_storage',
                defaultMessage: 'Energy storage',
                description: 'Market label for energy storage industry',
            });
        case Markets.INDUSTRIAL:
            return intl.formatMessage({
                id: 'markets.industrial',
                defaultMessage: 'Industrial',
                description: 'Market label for industrial applications',
            });
        case Markets.TELECOM:
            return intl.formatMessage({
                id: 'markets.telecom',
                defaultMessage: 'Telecom',
                description: 'Market label for telecommunications industry',
            });
        case Markets.MOBILITY:
            return intl.formatMessage({
                id: 'markets.mobility',
                defaultMessage: 'Mobility',
                description: 'Market label for mobility applications',
            });
        default:
            return market;
    }
};

const getRegistrationStatus = (
    event: Pick<
        RegistrationEventListItem,
        | 'published'
        | 'number_attendees'
        | 'subscription_status'
        | 'max_capacity'
        | 'user_meets_level_badge_requirements'
    >,
    hasUserData: boolean
): RegistrationLabel | null => {
    if (!hasUserData || !event) return null;

    const isEventPublished = event.published;

    const isUserAccepted = event.subscription_status === RegistrationStatus.ACCEPTED;

    const hasMaxCapacityReached = event.number_attendees >= event.max_capacity;

    const userRequiresBadges =
        !event.user_meets_level_badge_requirements?.meets_requirements &&
        !event.user_meets_level_badge_requirements?.meets_requirements_without_temp_badges;

    if (!isEventPublished) {
        return RegistrationLabel.UNPUBLISHED;
    }

    if (isUserAccepted) {
        return RegistrationLabel.REGISTERED;
    }

    if (hasMaxCapacityReached) {
        return RegistrationLabel.CLOSED;
    }

    if (userRequiresBadges) {
        return RegistrationLabel.BADGES_REQUIRED;
    }

    return null;
};

const getRegistrationStatusColor = (status: RegistrationLabel): ChipProps['color'] => {
    switch (status) {
        case RegistrationLabel.UNPUBLISHED:
            return 'signal-warning';
        case RegistrationLabel.TEMPORARY:
        case RegistrationLabel.REGISTERED:
            return 'primary-800';
        case RegistrationLabel.INELIGIBLE:
        case RegistrationLabel.CLOSED:
            return 'neutral-600';
        case RegistrationLabel.BADGES_REQUIRED:
            return 'primary-600';
        default:
            return 'default';
    }
};

const getRegistrationLabel = (status: RegistrationLabel, intl: IntlShape): string => {
    switch (status) {
        case RegistrationLabel.UNPUBLISHED:
            return intl.formatMessage({
                id: 'events.registration.unpublished',
                defaultMessage: 'Event Not Published',
                description: '/app/events/{id} - Registration status when event is not yet published',
            });
        case RegistrationLabel.REGISTERED:
            return intl.formatMessage({
                id: 'events.registration.registered',
                defaultMessage: 'You Are Registered',
                description: '/app/events/{id} - Registration status when user is successfully registered',
            });
        case RegistrationLabel.VISIBILITY_OFF:
            return intl.formatMessage({
                id: 'events.registration.visibility_off',
                defaultMessage: 'Invisible',
                description: '/app/events/{id} - Registration status when event is hidden',
            });
        case RegistrationLabel.INELIGIBLE:
            return intl.formatMessage({
                id: 'events.registration.ineligible',
                defaultMessage: 'Ineligible For Registration',
                description: '/app/events/{id} - Registration status when user cannot register',
            });
        case RegistrationLabel.CLOSED:
            return intl.formatMessage({
                id: 'events.registration.closed',
                defaultMessage: 'Registration Closed',
                description: '/app/events/{id} - Registration status when registration period has ended',
            });
        case RegistrationLabel.BADGES_REQUIRED:
            return intl.formatMessage({
                id: 'events.registration.badges_required',
                defaultMessage: 'See Requirements',
                description: '/app/events/{id} - Registration status when user needs specific badges',
            });
        case RegistrationLabel.TEMPORARY:
            return intl.formatMessage({
                id: 'events.registration.temporary',
                defaultMessage: 'You Are Temporarily Registered',
                description: '/app/events/{id} - Registration status when user has a temporary status',
            });
        default:
            return '';
    }
};

const EVENT_TIMEFRAME: SelectOption[] = [
    {
        value: 'past',
        label: 'Past events',
    },
    {
        value: 'upcoming',
        label: 'Upcoming Events',
    },
];

const EVENT_TABLE_HEADERS_CLASSNAME = 'font-medium !text-sm leading-[24px]';
const EVENT_TABLE_HEADERS: TableHeader<ManagementAnyEvent>[] = [
    { id: 'name', label: 'NAME', className: EVENT_TABLE_HEADERS_CLASSNAME },
    { id: 'type', label: 'EVENT TYPE', className: EVENT_TABLE_HEADERS_CLASSNAME, width: '15%' },
    { id: 'date_start', label: 'START/END DATE', className: EVENT_TABLE_HEADERS_CLASSNAME, width: '15%' },
    {
        id: 'max_capacity',
        label: 'GUESTS',
        title: 'Number of Attendees/Max Capacity',
        className: EVENT_TABLE_HEADERS_CLASSNAME,
        width: '10%',
    },
    { id: 'published', label: 'PUBLISHED', className: EVENT_TABLE_HEADERS_CLASSNAME, width: '10%' },
    {
        id: 'level_badge_requirements',
        label: 'REQUIRED BADGES',
        className: EVENT_TABLE_HEADERS_CLASSNAME,
        width: '20%',
    },
];

const EVENT_ATTENDEES_TABLE_HEADERS: TableHeader<
    BEAttendee & {
        received_emails: 'string';
        group__name: 'string';
        user__food_preference: 'string';
    }
>[] = [
    { id: 'user__full_name', label: 'NAME', width: '20%' },
    { id: 'user__company_name', label: 'COMPANY' },
    { id: 'status', label: 'STATUS', width: '105px' },
    { id: 'group__name', label: 'GROUP' },
    { id: 'accepted_at', label: 'REG. DATE' },
    { id: 'user__food_preference', label: 'DINNER / FOOD PREFERENCE' },
    { id: 'received_emails', label: 'RECEIVED EMAILS', disabled: true },
];

const EVENT_ATTENDEES_SIGN_IN_TABLE_HEADERS: TableHeader<
    BEAttendee & {
        received_emails: 'string';
        group__name: 'string';
        user__food_preference: 'string';
    }
>[] = [
    { id: 'user__full_name', label: 'NAME' },
    { id: 'user__company_name', label: 'COMPANY', width: '400px' },
    { id: 'present', label: 'SIGN IN STATUS' },
    { id: 'group__name', label: 'GROUP' },
];

const EVENT_INVITE_PEOPLE_TABLE_HEADERS: TableHeader<IEventUser>[] = [
    { id: 'full_name', label: 'NAME' },
    { id: 'company_name', label: 'COMPANY' },
    { id: 'role', label: 'ROLE' },
    { id: 'date_joined', label: 'REGISTRATION DATE' },
];

const EVENT_ADD_COHOST_HEADERS: TableHeader<IUser>[] = [
    { id: 'full_name', label: 'NAME', sortable: false },
    { id: 'company_name', label: 'COMPANY', sortable: false },
    { id: 'role', label: 'ROLE', sortable: false },
];

const BLACKLIST_USER_SELECTION_HEADERS: TableHeader<BlacklistTableHeader>[] = [
    { label: 'NAME', id: 'subscriber__user__full_name', width: '35%' },
    { label: 'COMPANY', id: 'company' },
    { label: 'ROLE', id: 'role', width: '25%', sortable: false },
];

const EVENT_SCORELIST: TableHeader<BEAttendee>[] = [
    { id: 'user__full_name', label: 'INVITEE NAME' },
    { id: 'event_followup_evaluation', label: 'FEEDBACK SCORE' },
];

const EVENT_ADD_GUEST_BLACKLIST_SEARCH_BY_OPTIONS: SelectOption<string>[] = [
    {
        value: 'search',
        label: 'Name',
    },
    {
        value: 'search_email',
        label: 'Email',
    },
    {
        value: 'search_company_name',
        label: 'Company',
    },
];

const PRIVACY_FILTER_OPTIONS: SelectOption[] = [
    {
        label: 'Public',
        value: 'public',
    },
    {
        label: 'Private',
        value: 'private',
    },
];

const EVENT_TABLE_ROWS_PER_PAGE = 10;

const EVENT_STATUS: SelectOption[] = [
    {
        label: 'Published',
        value: 'true',
    },
    {
        label: 'Unpublished',
        value: 'false',
    },
];

const EVENT_TYPES: (SelectOption & { value: EventTypes; title: string; shortLabel: string })[] = [
    {
        value: EventTypes.PUBLIC_ONSITE,
        label: 'Public Onsite Event',
        shortLabel: 'Onsite Event',
        title: 'Public - Onsite Event',
    },
    {
        value: EventTypes.PUBLIC_VIRTUAL,
        label: 'Public Virtual Event',
        shortLabel: 'Virtual Event',
        title: 'Public - Virtual Event',
    },
    {
        value: EventTypes.PRIVATE_ONSITE,
        label: 'Private Onsite Event',
        shortLabel: 'Onsite Event',
        title: 'Private - Onsite Event',
    },
    {
        value: EventTypes.PRIVATE_VIRTUAL,
        label: 'Private Virtual Event',
        shortLabel: 'Virtual Event',
        title: 'Private - Virtual Event',
    },
];

const EVENT_DATE_FILTERS: SelectOption[] = [
    {
        label: 'Date start:',
        value: 'date_range_start',
    },
    {
        label: 'Date end:',
        value: 'date_range_end',
    },
];

const EVENT_ORGANIZER: SelectOption[] = [{ label: 'Me', value: 'me' }];

const ATTENDEE_PRESENT: SelectOption[] = [
    {
        label: 'Signed In',
        value: 'yes',
    },
    {
        label: 'Didn’t Attend',
        value: 'no',
    },
    {
        label: 'Awaiting',
        value: 'na',
    },
];

const ATTENDEE_STATUS: SelectOption[] = [
    {
        label: 'Accepted',
        value: RegistrationStatus.ACCEPTED,
    },
    {
        label: 'Declined',
        value: RegistrationStatus.DECLINED,
    },
    {
        label: 'Canceled',
        value: RegistrationStatus.CANCELED,
    },
    {
        label: 'Able to Register',
        value: RegistrationStatus.ABLE_TO_REGISTER,
    },
    {
        label: 'Pending',
        value: RegistrationStatus.PENDING,
    },
    {
        label: 'Temporary Registration',
        value: 'temporary_registration',
    },
];

const ATTENDEE_APPLICATION_STATUS: SelectOption[] = [
    {
        label: 'Accepted',
        value: RegistrationApplicationStatus.ACCEPTED,
    },
    {
        label: 'Rejected',
        value: RegistrationApplicationStatus.DECLINED,
    },
    {
        label: 'Pending Approval',
        value: RegistrationApplicationStatus.PENDING,
    },
    {
        label: 'None',
        value: RegistrationApplicationStatus.NONE,
    },
];

const EVENT_SIGN_IN_GUESTS_PRINTER_HEADERS: TableHeader<EventPrinter>[] = [
    { id: 'printer_friendly_name', label: 'PRINTER NAME', sortable: false },
];

const EVENT_SIGN_IN_GUESTS_HEADERS: TableHeader<IUser & Pick<IAttendee, 'status'>>[] = [
    { id: 'full_name', label: 'NAME', sortable: false },
    { id: 'company_name', label: 'COMPANY', sortable: false },
    { id: 'role', label: 'ROLE', sortable: false },
    { id: 'status', label: 'SIGN IN STATUS', sortable: false },
];

const getEventType = (location: EventLocation, isPrivate: boolean) => {
    switch (location) {
        case EventLocation.ONSITE:
            if (isPrivate) return EventTypes.PRIVATE_ONSITE;
            return EventTypes.PUBLIC_ONSITE;
        case EventLocation.VIRTUAL:
            if (isPrivate) return EventTypes.PRIVATE_VIRTUAL;
            return EventTypes.PUBLIC_VIRTUAL;
    }
};

const filterAttendeesByName = (attendee: Partial<IAttendee>, value?: string[]): attendee is IAttendee =>
    value?.length ? attendee.user?.full_name?.toLowerCase().includes(value[0].toLowerCase()) ?? true : true;

const filterAttendeesByCompany = (attendee: Partial<IAttendee>, value?: string[]): attendee is IAttendee =>
    value?.length ? attendee.user?.company_name?.toLowerCase().includes(value[0].toLowerCase()) ?? true : true;

const filterAttendeesByRole = (attendee: Partial<IAttendee>, value?: string[]): attendee is IAttendee =>
    value?.length ? value.map((x) => x.toLowerCase()).includes(attendee.user?.role?.trim().toLowerCase() ?? '') : true;

const filterAttendeesByCountry = (attendee: Partial<IAttendee>, value?: string[]): attendee is IAttendee =>
    value?.length
        ? value.map((x) => x.toLowerCase()).includes(attendee.user?.country?.name?.trim().toLowerCase() ?? '')
        : true;

const filterAttendeesByRegistrationDate = (attendee: Partial<IAttendee>, value?: string[]): attendee is IAttendee => {
    if (!value?.length) return true;

    const dateValue = new Date(value[0]);
    dateValue.setHours(0, 0, 0);
    const eventStartDate = !!attendee.accepted_at ? new Date(attendee.accepted_at) : null;
    eventStartDate?.setHours(0, 0, 0);

    return value?.length ? (eventStartDate ? dateValue.getTime() <= eventStartDate.getTime() : false) : true;
};

const EVENT_ATTENDEES_FILTERS: Filter<IAttendee>[] = [
    { id: 'name', callback: filterAttendeesByName },
    {
        id: 'company',
        callback: filterAttendeesByCompany,
    },
    {
        id: 'role',
        callback: filterAttendeesByRole,
    },
    {
        id: 'country',
        callback: filterAttendeesByCountry,
    },
    {
        id: 'registration_date',
        callback: filterAttendeesByRegistrationDate,
    },
];

const getEventTypeFromCreation = (event: IEventCreation) => {
    if (event.type === EventLocation.ONSITE) {
        return event.privacy === 'private' ? EventTypes.PRIVATE_ONSITE : EventTypes.PUBLIC_ONSITE;
    } else {
        return event.privacy === 'private' ? EventTypes.PRIVATE_VIRTUAL : EventTypes.PUBLIC_VIRTUAL;
    }
};

const removeTimezoneOffset = (date: string) =>
    date.includes('+') || date.includes('-') ? date.replace(timezoneOffsetRegex, '') : date;

const getTimezoneOffset = (date: string, timeZone: string, locale = 'en-US') => {
    // Some browsers/WebViews (notably certain Android WebView versions) expose `Intl.DateTimeFormat`
    // but not `Intl.DateTimeFormat.supportedLocalesOf`, which the constructor relies on internally
    // and throws when missing. Guard against that instead of crashing.
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat?.supportedLocalesOf !== 'function') {
        return '+00:00';
    }

    // Create a Date object for the specified date
    const dt = new Date(date);

    try {
        // Create a formatter for the specified timezone
        const formatter = new Intl.DateTimeFormat(locale, {
            timeZone,
            hour12: false,
            timeZoneName: 'longOffset',
        });

        // Format the date to get the timezone offset
        const parts = formatter.formatToParts(dt);

        // Extract the timezone offset
        const offsetPart = parts.find((part) => part.type === 'timeZoneName')?.value;

        // Extract the sign, hours, and minutes from the offset string
        const match = offsetPart?.match(/([+-])(\d{2}):(\d{2})/);
        if (match) {
            const [, sign, hours, minutes] = match;
            return `${sign}${hours}:${minutes}`;
        }
    } catch {
        // Fall back below if the runtime's Intl implementation misbehaves
    }

    return '+00:00'; // Return a default value if timezone offset cannot be determined
};

const getDateWithDST = (date: string, timeZone: string) => {
    const parsedDate = removeTimezoneOffset(date);
    return parsedDate + getTimezoneOffset(parsedDate, timeZone);
};

const EVENT_TYPE_ABBREVIATION = {
    [EventTypes.PRIVATE_ONSITE]: 'pe',
    [EventTypes.PUBLIC_ONSITE]: 'oe',
    [EventTypes.PRIVATE_VIRTUAL]: 'pw',
    [EventTypes.PUBLIC_VIRTUAL]: 'ow',
};

const EVENT_TYPE_KEYS = Object.keys(EVENT_TYPE_ABBREVIATION);

const getMarketIcons: (intl: IntlShape) => Record<
    MarketSlugs,
    {
        icon: React.FunctionComponent<
            React.SVGProps<SVGSVGElement> & {
                title?: string;
            }
        >;
        description: string;
        url: string;
    }
> = (intl: IntlShape) => ({
    [MarketSlugs.PROFESSIONAL_VEHICLES]: {
        icon: ProfessionalVehicles,
        description: intl.formatMessage({
            id: 'market.professionalVehicles.description',
            defaultMessage:
                'Our up-to-date solutions counter most typical automotive issues and can be fine-tuned to your specific needs.',
        }),
        url: 'https://www.victronenergy.com/markets/professional-vehicles',
    },
    [MarketSlugs.RECREATIONAL_VEHICLES]: {
        icon: RecreationalVehicles,
        description: intl.formatMessage({
            id: 'market.recreationalVehicles.description',
            defaultMessage:
                'Go off-grid with confidence while enjoying the comforts of home.Stay in control with our smart and reliable power system.',
        }),
        url: 'https://www.victronenergy.com/markets/recreational-vehicles',
    },
    [MarketSlugs.PROFESSIONAL_MARINE]: {
        icon: ProfessionalMarine,
        description: intl.formatMessage({
            id: 'market.professionalMarine.description',
            defaultMessage:
                "Whether you're going on a short fishing trip or planning to sail across the ocean, you'll need reliable power to enjoy your adventure to the fullest.",
        }),
        url: 'https://www.victronenergy.com/markets/marine',
    },
    [MarketSlugs.LEISURE_MARINE]: {
        icon: LeisureMarine,
        description: intl.formatMessage({
            id: 'market.leisureMarine.description',
            defaultMessage:
                "Whether you're sailing away for the weekend or planning a trip around the world, big chance the very same Victron solution could support both.",
        }),
        url: 'https://www.victronenergy.com/markets/marine/sailing-yacht',
    },
    [MarketSlugs.OFFGRID_BACKUP]: {
        icon: OffgridBackup,
        description: intl.formatMessage({
            id: 'market.offgridBackup.description',
            defaultMessage:
                "Victron's off-grid abilities are simply unmatched, especially when it comes down to easy and intuitive remote management of highly sophisticated systems.",
        }),
        url: 'https://www.victronenergy.com/markets/off-grid',
    },
    [MarketSlugs.ENERGY_STORAGE]: {
        icon: EnergyStorage,
        description: intl.formatMessage({
            id: 'market.energyStorage.description',
            defaultMessage:
                'As more solar and wind power comes on line, it becomes increasingly difficult and expensive, to ensure stability of the grid.',
        }),
        url: 'https://www.victronenergy.com/markets/self-consumption-and-home-energy-storage',
    },
    [MarketSlugs.INDUSTRIAL]: {
        icon: Industrial,
        description: intl.formatMessage({
            id: 'market.industrial.description',
            defaultMessage:
                'The industrial market covers a broad array of organisations where Victron products can be used. We have offered support to governments, to energy suppliers and mining companies.',
        }),
        url: 'https://www.victronenergy.com/markets/industrial',
    },
    [MarketSlugs.TELECOM]: {
        icon: Telecom,
        description: intl.formatMessage({
            id: 'market.telecom.description',
            defaultMessage:
                'We offer a wide range of Hybrid Power Solutions for off-grid Base Transmission Stations (BTS) with integrated Network Operation Centres (NOC).',
        }),
        url: 'https://www.victronenergy.com/markets/telecom',
    },
    [MarketSlugs.MOBILITY]: {
        icon: Mobility,
        description: intl.formatMessage({
            id: 'market.mobility.description',
            defaultMessage:
                'The mobility market is one of the markets for which power supply is of the utmost importance. Particularly scoot mobile and electrical wheelchair users depend on reliable power.',
        }),
        url: 'https://www.victronenergy.com/markets/mobility',
    },
});

const convertDataToPercentage = <T extends { name: string; count: number }>(data: T[], minPercentage?: number) => {
    const total = data.reduce((sum, s) => sum + s.count, 0);

    // Step 1: Convert to initial percentages
    const rawPercentages = data.map((s) => ({
        ...s,
        percentage: (s.count * 100) / total,
    }));

    if (minPercentage) {
        // Step 2: Clamp anything below the minimium and calculate how much we added
        let extraNeeded = 0;

        const clamped = rawPercentages.map((s) => {
            if (s.percentage < minPercentage && s.count > 0) {
                extraNeeded += minPercentage - s.percentage;
                return { ...s, percentage: minPercentage };
            }
            return s;
        });

        // Step 3: Reduce the other stats proportionally
        const totalAboveMin = clamped
            .filter((s) => s.percentage > minPercentage)
            .reduce((sum, s) => sum + s.percentage, 0);

        const adjusted = clamped.map((s) => {
            if (s.percentage > minPercentage) {
                const reduction = (s.percentage / totalAboveMin) * extraNeeded;
                return {
                    ...s,
                    percentage: s.percentage - reduction,
                };
            }
            return s;
        });

        return adjusted.map((s) => ({
            ...s,
            percentage: Math.round(s.percentage * 100) / 100,
        }));
    } else {
        return rawPercentages.map((s) => ({
            ...s,
            percentage: Math.round(s.percentage * 100) / 100,
        }));
    }
};

const getOppositeEventType = (type: EventTypes) => {
    switch (type) {
        case EventTypes.PRIVATE_ONSITE:
            return EventTypes.PRIVATE_VIRTUAL;
        case EventTypes.PUBLIC_ONSITE:
            return EventTypes.PUBLIC_VIRTUAL;
        case EventTypes.PRIVATE_VIRTUAL:
            return EventTypes.PRIVATE_ONSITE;
        case EventTypes.PUBLIC_VIRTUAL:
            return EventTypes.PUBLIC_ONSITE;
    }
};

const getEventRequiredBadges = (list: EventLevelBadgeRequirement | LogicalOperator | []) => {
    const badges: EventLevelBadgeRequirement[] = [];
    if (Array.isArray(list)) {
        return badges;
    }

    // DFS traversal to extract badges
    const traverse = (node: EventLevelBadgeRequirement | LogicalOperator) => {
        if ('logical_operator' in node) {
            traverse(node.op1);
            traverse(node.op2);
        } else {
            // Avoid pushing duplicates
            if (badges.some((x) => x.id === node.id)) {
                return;
            }
            badges.push(node);
        }
    };

    traverse(list);

    return badges;
};

const parseISODuration = (isoString: string) => {
    const regex = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;
    const matches = isoString.match(regex);
    if (!matches) return {};

    // matches[1] = years, matches[2] = months, etc.
    return {
        years: matches[1] ? parseInt(matches[1]) : 0,
        months: matches[2] ? parseInt(matches[2]) : 0,
        days: matches[3] ? parseInt(matches[3]) : 0,
        hours: matches[4] ? parseInt(matches[4]) : 0,
        minutes: matches[5] ? parseInt(matches[5]) : 0,
        seconds: matches[6] ? parseInt(matches[6]) : 0,
    };
};

const formatISODurationToFullString = (isoString: string) => {
    const duration = parseISODuration(isoString);

    if (!duration) return '';

    let output = '';
    const parts = Object.keys(duration);

    for (const part of parts) {
        const number = duration[part as keyof typeof duration];
        if (Number(number) > 0) {
            output += `${number} ${capitalizeFirstLetter(pluralize(part, number))}, `;
        }
    }

    return output.slice(0, -2); // Remove trailing ', '
};

const calcStatusFilter = (statuses: string[]) => {
    const hasAccepted = statuses?.includes(RegistrationStatus.ACCEPTED);
    const hasTemporary = statuses?.includes('temporary_registration');

    if (!statuses || statuses.length === 0) {
        return {
            status: [],
            is_temporary_registration: [],
        };
    }

    const baseStatuses = statuses.filter(
        (status) => ![RegistrationStatus.ACCEPTED, 'temporary_registration'].includes(status)
    );

    if (hasAccepted && hasTemporary) {
        return {
            status: [...baseStatuses, RegistrationStatus.ACCEPTED],
            is_temporary_registration: [],
        };
    } else if (hasAccepted && !hasTemporary) {
        return {
            status: [...baseStatuses, RegistrationStatus.ACCEPTED],
            is_temporary_registration: ['false'],
        };
    } else if (!hasAccepted && hasTemporary) {
        return {
            status: [...baseStatuses, RegistrationStatus.ACCEPTED],
            is_temporary_registration: ['true'],
        };
    } else {
        return {
            status: baseStatuses,
            is_temporary_registration: [],
        };
    }
};

const getActivityState = (status: ISubscriberActivity['status']) => {
    switch (status) {
        case 'accepted':
            return {
                label: 'Registered',
                color: 'alert-info.main',
                bgColor: 'bg-alert-info-bg',
            };
        case 'declined':
        case 'pending':
            return {
                label: 'Pending',
                color: 'dark-yellow.main',
                bgColor: 'bg-warning-light',
            };
        case 'on_waiting_list':
            return {
                label: 'In Queue',
                color: 'dark-yellow.main',
                bgColor: 'bg-warning-light',
            };
    }
};

const getWaitingListChipStatus = (
    status: WaitingListEntry['status']
): {
    label: string;
    color: string;
    bgColor: ChipProps['color'];
} => {
    switch (status) {
        case 'in_queue':
            return {
                label: 'In Queue',
                color: 'dark-yellow.main',
                bgColor: 'light-warning',
            };
        case 'able_to_register':
            return {
                label: 'Able to Register',
                color: 'alert-info.main',
                bgColor: 'alert-info-bg',
            };
        case 'accepted':
            return {
                label: 'Accepted',
                color: 'success.main',
                bgColor: 'light-success',
            };
        case 'declined':
            return {
                label: 'Declined',
                color: 'dark-red.main',
                bgColor: 'light-error',
            };
        case 'expired':
            return {
                label: 'Failed To Register',
                color: 'dark-red.main',
                bgColor: 'light-error',
            };
    }
};

const getAttendeeRegistrationStatusColor = (attendee?: Partial<IAttendee>): ChipProps['color'] => {
    if (attendee?.is_temporary_registration) {
        return 'light-warning';
    }
    switch (attendee?.status) {
        case RegistrationStatus.ACCEPTED:
            return 'light-confirm';
        case RegistrationStatus.DECLINED:
        case RegistrationStatus.CANCELED:
            return 'light-error';
        case RegistrationStatus.PENDING:
            return 'light-warning';
        case RegistrationStatus.ABLE_TO_REGISTER:
        default:
            return 'alert-info-bg';
    }
};

const getAttendeePresentStatusColor = (present?: PresentStatus) => {
    switch (present) {
        case 'na':
            return 'light-warning';
        case 'yes':
            return 'light-confirm';
        case 'no':
            return 'light-error';
        default:
            return 'default';
    }
};

const renderCountryOption = (item: Option) => (
    <ListItemText
        slotProps={{
            primary: {
                className: 'flex items-center gap-2 text-sm',
            },
        }}
    >
        <Flag flag={(item.value as string)?.toLowerCase()} />
        <p>{item.label}</p>
    </ListItemText>
);

const renderCountryValue = (countryCode: string, countries: Option[]) => {
    const country = countries.find((c) => c.value === countryCode);
    const countryName = country ? country.label : countryCode;

    return (
        <div className="flex items-center gap-2">
            <Flag flag={countryCode?.toLowerCase()} />
            <span className="font-inter text-md">{countryName}</span>
        </div>
    );
};

export {
    ATTENDEE_APPLICATION_STATUS,
    ATTENDEE_PRESENT,
    ATTENDEE_STATUS,
    BLACKLIST_USER_SELECTION_HEADERS,
    calcStatusFilter,
    convertDataToPercentage,
    EVENT_ADD_COHOST_HEADERS,
    EVENT_ADD_GUEST_BLACKLIST_SEARCH_BY_OPTIONS,
    EVENT_ATTENDEES_FILTERS,
    EVENT_ATTENDEES_SIGN_IN_TABLE_HEADERS,
    EVENT_ATTENDEES_TABLE_HEADERS,
    EVENT_DATE_FILTERS,
    EVENT_INVITE_PEOPLE_TABLE_HEADERS,
    EVENT_ORGANIZER,
    EVENT_SCORELIST,
    EVENT_SIGN_IN_GUESTS_HEADERS,
    EVENT_SIGN_IN_GUESTS_PRINTER_HEADERS,
    EVENT_STATUS,
    EVENT_TABLE_HEADERS,
    EVENT_TABLE_ROWS_PER_PAGE,
    EVENT_TIMEFRAME,
    EVENT_TYPE_ABBREVIATION,
    EVENT_TYPE_KEYS,
    EVENT_TYPES,
    formatISODurationToFullString,
    getActivityState,
    getAttendeePresentStatusColor,
    getAttendeeRegistrationStatusColor,
    getDateWithDST,
    getEventRequiredBadges,
    getEventType,
    getEventTypeFromCreation,
    getEventTypeName,
    getEventTypeShortLabel,
    getEventTypeTitle,
    getLanguageSpokenLabel,
    getMarketIcons,
    getMarketLabel,
    getOppositeEventType,
    getRegistrationLabel,
    getRegistrationStatus,
    getRegistrationStatusColor,
    getTimezoneOffset,
    getUntranslatedEventTypeTitle,
    getWaitingListChipStatus,
    PRIVACY_FILTER_OPTIONS,
    renderCountryOption,
    renderCountryValue,
};
